import { adminService } from '@/api/services';
import type { UnwrapData } from '@/api/types';

import type {
  IAbilitiesResponse,
  ICreateAbilityRequest,
  ICreateAbilityResponse,
  IDeleteAbilityRequest,
  IReorderAbilitiesRequest,
  IUpdateAbilityRequest,
  TUpdateAbilityResponse,
} from './types';

export const schoolAbilitiesService = adminService.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Получить навыки школы
     */
    getSchoolAbilities: builder.query<UnwrapData<IAbilitiesResponse>, { schoolId: number }>({
      query: ({ schoolId }) => ({
        method: 'GET',
        url: `/schools/${schoolId}/abilities`,
      }),
      providesTags: (_, __, { schoolId }) => [{ type: 'SchoolAbilities', id: schoolId }],
      transformResponse: (response: IAbilitiesResponse): UnwrapData<IAbilitiesResponse> => response.data,
    }),
    /**
     * Создать навык школы
     */
    createSchoolAbility: builder.mutation<ICreateAbilityResponse, ICreateAbilityRequest>({
      query: ({ data, schoolId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/abilities`,
        body: {
          data,
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolAbilities', id: schoolId }],
    }),
    /**
     * Изменить навык школы
     */
    updateSchoolAbility: builder.mutation<TUpdateAbilityResponse, IUpdateAbilityRequest>({
      query: ({ data, schoolId, abilityId }) => ({
        method: 'PATCH',
        url: `/schools/${schoolId}/abilities/${abilityId}`,
        body: {
          data,
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolAbilities', id: schoolId }],
    }),
    /**
     * Удалить навык школы
     */
    deleteSchoolAbility: builder.mutation<void, IDeleteAbilityRequest>({
      query: ({ schoolId, abilityId }) => ({
        method: 'DELETE',
        url: `/schools/${schoolId}/abilities/${abilityId}`,
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolAbilities', id: schoolId }],
    }),
    /**
     * Изменить порядок навыков школы
     */
    reorderSchoolAbilities: builder.mutation<IAbilitiesResponse, IReorderAbilitiesRequest>({
      query: ({ schoolId, data }) => ({
        method: 'PATCH',
        url: `/schools/${schoolId}/abilities/reorder`,
        body: {
          data,
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolAbilities', id: schoolId }],
    }),
  }),
});

export const {
  useGetSchoolAbilitiesQuery,
  useCreateSchoolAbilityMutation,
  useUpdateSchoolAbilityMutation,
  useDeleteSchoolAbilityMutation,
  useReorderSchoolAbilitiesMutation,
} = schoolAbilitiesService;
